import { Typography, Box, Button, Grid, TextField, Tooltip, IconButton, Snackbar, Alert } from '@mui/material';
import { NoContent, ReactTable, PercentTextField, Datepicker } from '../../../components/shared';
import { COLUMNS } from '../../../constants/tableColumns';
import useStyles from './style';
import { useEffect, useState, useRef } from 'react';
import { getDateFormat } from '../../../utils/common-methods';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { formatTaxPercentValue } from '../../../utils/common-methods';
import { parseConflictMessage } from '../../../utils/parseConflictMessage';
import {
  useGetAreaTaxQuery,
  useCreateAreaTaxMutation,
  useDeleteAreaTaxMutation,
} from '../../../services/SalesTaxHistory/AreaTaxService';
import {
  useGetStateTaxQuery,
  useCreateStateTaxMutation,
  useDeleteStateTaxMutation,
} from '../../../services/SalesTaxHistory/StateTaxService';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;

const EMPTY_ROW_DATA = {
  stateTaxRate: 0,
  areaTaxRate: 0,
  areaTaxLocation: null,
  inEffectFrom: '',
  inEffectTo: '',
  note: '',
};

//component for Area tax history and state tax history
export default function SalesTaxHistoryModal({
  taxModal,
  sublocation,
  stateIdObj,
  onAfterSave,
  disallowNegativeStateTax = false,
  validAreaTaxLocationId = false,
}) {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const previousPage = useRef(DEFAULT_PAGE);
  const previousPerPage = useRef(DEFAULT_PER_PAGE);
  const isStateTaxHistory = taxModal === 'State';
  const isAreaTaxHistory = taxModal === 'Area';
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isStateIdAvailable = !!stateIdObj?.stateId;

  const [tableData, setTableData] = useState([]);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [rowData, setRowData] = useState(EMPTY_ROW_DATA);
  const [disableInEffectToDate, setDisableInEffectToDate] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [stateTaxError, setStateTaxError] = useState('');
  const [isFirstStateHistory, setIsFirstStateHistory] = useState(true);
  const updatedById = useSelector((state) => state.user?.userDetails?.id);

  const {
    currentData: areaData,
    isSuccess: areaSuccess,
    isFetching: areaFetching,
    isLoading: areaLoading,
    isError: areaError,
  } = useGetAreaTaxQuery(
    {
      searchParams: '',
      page: currentPage,
      perPage: perPageNumber,
      id: sublocation?.areaTaxLocationId,
    },
    {
      skip: !isAreaTaxHistory || !sublocation?.areaTaxLocationId, // Skip when areaTaxLocationId is not available
      refetchOnMountOrArgChange: false, // Prevent automatic refetches
    },
  );

  const {
    currentData: stateData,
    isSuccess: stateSuccess,
    isFetching: stateFetching,
    isLoading: stateLoading,
    isError: stateError,
  } = useGetStateTaxQuery(
    {
      searchParams: '',
      page: currentPage,
      perPage: perPageNumber,
      id: stateIdObj?.stateId,
    },
    {
      skip: !isStateTaxHistory || !stateIdObj?.stateId,
      refetchOnMountOrArgChange: false, // Prevent automatic refetches
    },
  );

  const [createAreaTax] = useCreateAreaTaxMutation();
  const [createStateTax] = useCreateStateTaxMutation();
  const [deleteAreaTax] = useDeleteAreaTaxMutation();
  const [deleteStateTax] = useDeleteStateTaxMutation();

  // Update tableData when API data is received
  useEffect(() => {
    if (isStateTaxHistory && stateSuccess && stateData?.data) {
      const mappedData = stateData.data?.stateTaxHistory?.map((item) => ({
        id: item.id,
        inEffectFrom: item.inEffectFromDate ? getDateFormat(item.inEffectFromDate) : '',
        inEffectTo: item.inEffectToDate ? getDateFormat(item.inEffectToDate) : '',
        stateTax: formatTaxPercentValue(item.stateTax),
        state: item.state?.name || '',
        notes: item.notes || '',
        indicator: item.indicator || 'Active',
      }));

      setTableData(mappedData);
      setTotalCount(stateData?.pagination?.total || 0);
    } else if (!isStateTaxHistory && areaSuccess && areaData?.data) {
      const mappedData = areaData.data?.areaTaxHistory?.map((item) => ({
        id: item.id,
        inEffectFrom: item.inEffectFromDate ? getDateFormat(item.inEffectFromDate) : '',
        inEffectTo: item.inEffectToDate ? getDateFormat(item.inEffectToDate) : '',
        areaTax: formatTaxPercentValue(item.areaTax),
        areaTaxLocation: item.areaTaxLocation || '',
        areaTaxLocationId: item.areaTaxLocationId || '',
        notes: item.notes || '',
        indicator: item.indicator || 'Active',
      }));

      setTableData(mappedData);
      setTotalCount(areaData?.pagination?.total || 0);
    }
  }, [isStateTaxHistory, stateSuccess, stateData, areaSuccess, areaData]);

  // Determine if the first record is the initial tax history
  useEffect(() => {
    if (tableData.length > 0) {
      setIsFirstStateHistory(false);
    }
  }, [tableData]);

  // This function enables the "In Effect To" date when adding a new record
  const handleAddNewClick = () => {
    setShowAddNewForm(true);
    setDisableInEffectToDate(false);

    // Set the areaTaxLocation from sublocation
    if (sublocation && sublocation.areaTaxLocation) {
      const locationObj = {
        value: sublocation.areaTaxLocation,
        text: sublocation.areaTaxLocationName || sublocation.areaTaxLocation,
      };

      setRowData((prev) => ({
        ...prev,
        areaTaxLocation: locationObj,
      }));
    }
  };

  const handleChange = (field, value) => {
    setRowData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const pageChangeHandler = (newPage) => {
    if (newPage !== currentPage && newPage !== previousPage.current) {
      previousPage.current = newPage;
      setCurrentPage(newPage);
    }
  };

  const perPageChangeHandler = (newPerPage) => {
    if (newPerPage !== perPageNumber && newPerPage !== previousPerPage.current) {
      previousPerPage.current = newPerPage;
      setCurrentPage(DEFAULT_PAGE);
      setPerPageNumber(newPerPage);
    }
  };

  // Show snackbar utility function
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    // Fixed validation logic using ternary operator
    const valid = isStateTaxHistory
      ? {
          inEffectFrom: !!rowData.inEffectFrom,
          stateTaxRate: rowData.stateTaxRate !== undefined && rowData.stateTaxRate !== null,
        }
      : {
          inEffectFrom: !!rowData.inEffectFrom,
          areaTaxRate: rowData.areaTaxRate !== undefined && rowData.areaTaxRate !== null, // Allow negative values for area tax
        };

    // Check for negative state tax if disallowNegativeStateTax is true
    let hasNegativeStateTax = false;
    if (isStateTaxHistory && disallowNegativeStateTax && rowData.stateTaxRate < 0) {
      hasNegativeStateTax = true;
      setStateTaxError('State tax rate cannot be negative');
    } else {
      setStateTaxError('');
    }

    setDisableSave(Object.values(valid).includes(false) || hasNegativeStateTax);
  }, [rowData, isStateTaxHistory, disallowNegativeStateTax]);

  const handleSave = (overrideFlag = false) => {
    const newRecord = isStateTaxHistory
      ? {
          inEffectFromDate: rowData.inEffectFrom,
          inEffectToDate: rowData.inEffectTo || null,
          notes: rowData.note || '',
          subLocationId: sublocation?.id,
          stateId: stateIdObj?.stateId,
          updatedBy: updatedById,
          stateTax: rowData.stateTaxRate,
          override: overrideFlag,
        }
      : {
          inEffectFromDate: rowData.inEffectFrom,
          inEffectToDate: rowData.inEffectTo || null,
          notes: rowData.note || '',
          subLocationId: sublocation?.id,
          stateId: stateIdObj?.stateId,
          updatedBy: updatedById,
          areaTax: rowData.areaTaxRate,
          areaTaxLocation: sublocation.areaTaxLocation,
          areaTaxLocationId: sublocation.areaTaxLocationId,
          override: overrideFlag,
        };

    const saveFunction = isStateTaxHistory ? createStateTax : createAreaTax;

    saveFunction(newRecord)
      .unwrap()
      .then(() => {
        setShowAddNewForm(false);
        setRowData({
          stateTaxRate: 0,
          areaTaxRate: 0,
          areaTaxLocation: null,
          areaTaxLocationId: null,
          inEffectFrom: '',
          inEffectTo: '',
          note: '',
        });

        showSnackbar('Record saved successfully!');
        setCurrentPage(DEFAULT_PAGE);
        if (onAfterSave) onAfterSave();
      })
      .catch((error) => {
        // Handle custom status codes for conflict
        if (error.status === 409) {
          setErrorMessage(error.data?.message || 'A conflict occurred while saving the record.');
          setShowErrorModal(true);
        } else {
          showSnackbar('Failed to save record', 'error');
          console.error('Error saving record:', error);
        }
      });
  };

  const handleDelete = (index, rowToDelete) => {
    // Check if there's a next row (second row) that needs to be updated
    const nextRow = tableData.length > index + 1 ? tableData[index + 1] : null;

    const baseData = {
      inEffectFromDate: rowToDelete.inEffectFrom,
      inEffectToDate: rowToDelete.inEffectTo || null,
      notes: rowToDelete.note || '',
      subLocationId: sublocation?.id,
      stateId: stateIdObj?.stateId,
      updatedBy: updatedById,
    };

    const deleteData = isStateTaxHistory
      ? {
          ...baseData,
          id: rowToDelete?.id,
          stateTax: parseFloat(rowToDelete.stateTax.replace('%', '')) / 100,
          fromToDateDelete: true,
          nextRowId: nextRow?.id,
        }
      : {
          ...baseData,
          id: rowToDelete?.id,
          areaTax: parseFloat(rowToDelete.areaTax.replace('%', '')) / 100,
          areaTaxLocation: rowToDelete.areaTaxLocation,
          areaTaxLocationId: rowToDelete.areaTaxLocationId,
          fromToDateDelete: true,
          nextRowId: nextRow?.id,
        };

    const deleteFunction = isStateTaxHistory ? deleteStateTax : deleteAreaTax;

    deleteFunction(deleteData)
      .unwrap()
      .then(() => {
        showSnackbar('Record deleted successfully!');
      })
      .catch((error) => {
        showSnackbar('Failed to delete record', 'error');
        console.error('Error deleting record:', error);
      });
  };

  const handlePercentChange = (e) => {
    handleChange(e.name, e.value);
  };

  // Use a useEffect to initialize the areaTaxLocation when component mounts
  useEffect(() => {
    if (sublocation && sublocation.areaTaxLocation && !rowData.areaTaxLocation) {
      const locationObj = {
        value: sublocation.areaTaxLocation,
        text: sublocation.areaTaxLocationName || sublocation.areaTaxLocation,
      };

      setRowData((prev) => ({
        ...prev,
        areaTaxLocation: locationObj,
      }));
    }
  }, [sublocation]);

  // Add delete column for table
  const COLUMN_DELETE = {
    Header: '',
    accessor: 'delete',
    width: 50,
    Cell: ({ row }) => {
      const isFuture = row?.original?.indicator === 'future';
      return isFuture && currentPage === 1 && row?.index === 0 ? (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDelete(row.index, row.original)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null;
    },
  };

  const tableColumns = isStateTaxHistory
    ? [...COLUMNS.STATE_TAX_HISTORY, COLUMN_DELETE]
    : [...COLUMNS.AREA_TAX_HISTORY, COLUMN_DELETE];

  const isLoading = isStateTaxHistory ? stateLoading : areaLoading;
  const isFetching = isStateTaxHistory ? stateFetching : areaFetching;
  const isError = isStateTaxHistory ? stateError : areaError;

  const getRowErrorClass = (rowData) => (rowData?.original?.indicator === 'active' ? classes.activeRow : '');

  const handleInEffectFromChange = (date) => {
    const formattedDate = date ? getDateFormat(date) : '';
    setRowData((prev) => ({
      ...prev,
      inEffectFrom: formattedDate,
      inEffectTo: prev.inEffectTo && date && new Date(prev.inEffectTo) < date ? '' : prev.inEffectTo,
    }));
  };

  const handleInEffectToChange = (date) => {
    setRowData((prev) => ({
      ...prev,
      inEffectTo: date ? getDateFormat(date) : '',
    }));
  };

  const getDateObject = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const isAddButtonDisabled = isStateTaxHistory
    ? !isStateIdAvailable
    : !validAreaTaxLocationId && !sublocation.areaTaxLocationId;

  // Function to determine if a date should be disabled in the datepicker
  const shouldDisableDate = (date) => {
    if (!rowData.inEffectFrom) return false;
    const inEffectFromDate = new Date(rowData.inEffectFrom);
    return date < inEffectFromDate;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box display="flex" mb={2} justifyContent="space-between" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddNewClick}
          disabled={isAddButtonDisabled}
          sx={{ ml: 'auto' }}
        >
          Add New
        </Button>
      </Box>
      {showAddNewForm && (
        <>
          <Grid container spacing={2} mb={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <Datepicker
                label="In Effect From"
                name="inEffectFrom"
                id="inEffectFrom"
                inputFormat="MM/DD/YYYY"
                error={!rowData.inEffectFrom}
                onChange={handleInEffectFromChange}
                value={rowData?.inEffectFrom || null}
                clearable
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  pointerEvents:
                    isFirstStateHistory || disableInEffectToDate || !rowData.inEffectFrom ? 'none' : 'auto',
                  opacity: isFirstStateHistory || disableInEffectToDate || !rowData.inEffectFrom ? 0.8 : 1,
                }}
              >
                <Datepicker
                  label="In Effect To"
                  name="inEffectTo"
                  id="inEffectTo"
                  inputFormat="MM/DD/YYYY"
                  onChange={handleInEffectToChange}
                  value={rowData?.inEffectTo || null}
                  disabled={isFirstStateHistory || disableInEffectToDate || !rowData.inEffectFrom}
                  clearable={true}
                  minDate={getDateObject(rowData.inEffectFrom)}
                  minDateMessage="Date should be after In Effect From date"
                  shouldDisableDate={shouldDisableDate}
                />
              </Box>
            </Grid>

            {isStateTaxHistory && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="State"
                  variant="outlined"
                  name="state"
                  disabled
                  value={stateIdObj?.stateName?.name || stateIdObj?.stateName || ''}
                  onChange={(e) =>
                    setRowData({
                      ...rowData,
                      [e?.target?.name]: e?.target?.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              {isStateTaxHistory && stateTaxError ? (
                <Box sx={{ position: 'relative' }}>
                  <PercentTextField
                    label="State Tax"
                    value={rowData.stateTaxRate}
                    allowDecimal
                    decimalPlaces={4}
                    allowZero={true}
                    name="stateTaxRate"
                    onChange={handlePercentChange}
                    defaultValue
                    error={true}
                  />
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      position: 'absolute',
                      bottom: '-20px',
                      left: 0,
                      fontSize: '0.75rem',
                    }}
                  >
                    {stateTaxError}
                  </Typography>
                </Box>
              ) : (
                <PercentTextField
                  label={isStateTaxHistory ? 'State Tax' : 'Area Tax'}
                  value={isStateTaxHistory ? rowData.stateTaxRate : rowData.areaTaxRate}
                  allowDecimal
                  decimalPlaces={4}
                  allowZero={true}
                  name={isStateTaxHistory ? 'stateTaxRate' : 'areaTaxRate'}
                  onChange={handlePercentChange}
                  defaultValue
                  error={isStateTaxHistory && !!stateTaxError}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Note"
                variant="outlined"
                placeholder="Write a note..."
                name="note"
                value={rowData?.note || ''}
                onChange={(e) =>
                  setRowData({
                    ...rowData,
                    [e?.target?.name]: e?.target?.value,
                  })
                }
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={1} pb={2}>
                <Button variant="contained" disabled={disableSave} onClick={() => handleSave(false)}>
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setShowAddNewForm(false);
                    setRowData(EMPTY_ROW_DATA);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        {isStateTaxHistory && !isStateIdAvailable ? (
          <NoContent title="No State Available" desc={<span>No state is available for this sublocation.</span>} />
        ) : isAreaTaxHistory && !sublocation?.areaTaxLocationId ? (
          <NoContent
            title="No Area Tax Location Available"
            desc={<span>No area tax location is available for this sublocation.</span>}
          />
        ) : (
          <>
            {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
            {isError && (
              <NoContent
                title={`No ${isStateTaxHistory ? 'State' : 'Area'} Tax History Found`}
                desc={<span>No results found.</span>}
              />
            )}
            {!isLoading && !isError && !isFetching && (
              <>
                {/* Hide pagination when records are fewer than per page limit */}
                {totalCount <= 30 && (
                  <style>
                    {`
                      .MuiTablePagination-root {
                        display: none !important;
                      }
                    `}
                  </style>
                )}
                <ReactTable
                  data={tableData || []}
                  currentPage={currentPage - 1}
                  perPage={perPageNumber}
                  totalCount={totalCount}
                  columns={tableColumns}
                  rowClassName={(rowData) => getRowErrorClass(rowData)}
                  onPageChange={pageChangeHandler}
                  pagination={true}
                  manualPagination={true}
                  changeRowsPerPageOptions={true} // Always allow changing rows per page
                  onPerPageChange={perPageChangeHandler}
                  pageNumberForTablePagination={currentPage - 1} // Ensure consistent page state
                />
              </>
            )}
          </>
        )}
      </Box>
      <div>
        <ConfirmationModal
          isOpen={showErrorModal}
          title="Date Conflict"
          msg={parseConflictMessage(errorMessage)}
          buttons={[
            { text: 'Confirm', value: 'confirm' },
            { text: 'Cancel', value: 'cancel' },
          ]}
          onClick={(val) => {
            setShowErrorModal(false);
            if (val === 'confirm') {
              handleSave(true);
            }
          }}
        />
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
